import React from 'react';
import MaterialTable from 'material-table';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import AddIcon from '@material-ui/icons/Add';

const STATUS_BADGE_CLASS = {
  draft: 'badge-info',
  in_progress: 'badge-warning',
  completed: 'badge-success',
};

const TABLE_HEADER_STYLE = {
  backgroundColor: '#f7f9fc',
  color: '#6b7280',
  fontWeight: 700,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  padding: '12px 16px',
  borderTop: '1px solid #edf0f5',
  borderBottom: '1px solid #edf0f5',
  whiteSpace: 'nowrap',
};

const TABLE_CELL_STYLE = {
  padding: '12px 16px',
  fontSize: '0.88rem',
  color: '#1f2937',
};

export default class EhcServiceOrdersDataTable extends React.Component {
  constructor(props) {
    super(props);
    this.tableRef = React.createRef();
    this.searchTimer = null;

    this.columns = [
      {
        title: this.label('order_name', 'Order Name'),
        field: 'order_name',
        grouping: false,
        cellStyle: { ...TABLE_CELL_STYLE, minWidth: 180 },
        headerStyle: { ...TABLE_HEADER_STYLE, minWidth: 180 },
        render: rowData => (
          <>
            <a href={rowData.service_order_url} className="ehc-service-orders-card__link">
              {rowData.order_name}
            </a>
            {rowData.entry_mode === 'retrospective' && (
              <span className="badge badge-secondary ml-2">
                {this.label('entry_mode_retrospective', 'Retrospective')}
              </span>
            )}
          </>
        ),
      },
      {
        title: this.label('company', 'Company'),
        field: 'company_name',
        orderField: 'companies.name',
        cellStyle: { ...TABLE_CELL_STYLE, minWidth: 140 },
        headerStyle: { ...TABLE_HEADER_STYLE, minWidth: 140 },
        render: rowData => (
          rowData.company_url
            ? <a href={rowData.company_url} className="ehc-service-orders-card__link">{rowData.company_name}</a>
            : rowData.company_name
        ),
      },
      {
        title: this.label('package', 'Service Package'),
        field: 'service_package_name',
        orderField: 'support_services.name_id',
        cellStyle: { ...TABLE_CELL_STYLE, minWidth: 140 },
        headerStyle: { ...TABLE_HEADER_STYLE, minWidth: 140 },
      },
      {
        title: this.label('status', 'Status'),
        field: 'human_status',
        orderField: 'status',
        cellStyle: { ...TABLE_CELL_STYLE, width: 120 },
        headerStyle: { ...TABLE_HEADER_STYLE, width: 120 },
        render: rowData => (
          <span className={`badge ${STATUS_BADGE_CLASS[rowData.status] || 'badge-secondary'}`}>
            {rowData.human_status}
          </span>
        ),
      },
      {
        title: this.label('participants', 'Participants'),
        field: 'participants_count',
        type: 'numeric',
        grouping: false,
        cellStyle: { ...TABLE_CELL_STYLE, width: 90, textAlign: 'center' },
        headerStyle: { ...TABLE_HEADER_STYLE, width: 90, textAlign: 'center' },
      },
      {
        title: this.label('start_date', 'Start Date'),
        field: 'start_date',
        orderField: 'start_date',
        grouping: false,
        cellStyle: { ...TABLE_CELL_STYLE, width: 110, whiteSpace: 'nowrap' },
        headerStyle: { ...TABLE_HEADER_STYLE, width: 110 },
      },
      {
        title: this.label('end_date', 'End Date'),
        field: 'end_date',
        orderField: 'end_date',
        grouping: false,
        cellStyle: { ...TABLE_CELL_STYLE, width: 110, whiteSpace: 'nowrap' },
        headerStyle: { ...TABLE_HEADER_STYLE, width: 110 },
      },
      {
        title: this.label('created_at', 'Created At'),
        field: 'created_at',
        orderField: 'created_at',
        searchable: false,
        grouping: false,
        cellStyle: { ...TABLE_CELL_STYLE, width: 130, whiteSpace: 'nowrap' },
        headerStyle: { ...TABLE_HEADER_STYLE, width: 130 },
      },
      {
        title: this.label('updated_at', 'Updated At'),
        field: 'updated_at',
        orderField: 'updated_at',
        searchable: false,
        grouping: false,
        cellStyle: { ...TABLE_CELL_STYLE, width: 130, whiteSpace: 'nowrap' },
        headerStyle: { ...TABLE_HEADER_STYLE, width: 130 },
      },
    ];

    this.state = {
      statusFilter: '',
      statusCounts: {},
      totalCount: 0,
      searchInput: '',
      searchQuery: '',
    };
  }

  componentWillUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  label(key, fallback) {
    return this.props.labels?.[key] || fallback;
  }

  getTheme = () => {
    const isDarkMode = document.documentElement.getAttribute('data-mode-theme') === 'dark';
    return createTheme({
      palette: {
        type: isDarkMode ? 'dark' : 'light',
        primary: { main: '#41924E' },
        secondary: { main: '#2e7d38' },
        background: {
          paper: isDarkMode ? '#2a3441' : '#ffffff',
          default: isDarkMode ? '#1e2530' : '#f7f9fc',
        },
      },
      overrides: {
        MuiTableCell: {
          root: {
            borderBottom: isDarkMode ? '1px solid #3a4757' : '1px solid #f0f2f6',
          },
        },
      },
    });
  };

  fetchData = (query) => new Promise((resolve, reject) => {
    const requestedPage = query.page + 1;
    const params = new URLSearchParams({
      page: requestedPage,
      per_page: query.pageSize,
      q: this.state.searchQuery || '',
      sort_by: query.orderBy?.orderField ?? query.orderBy?.field ?? 'created_at',
      sort_order: query.orderDirection ?? 'desc',
    });

    if (this.props.company_id) params.set('company_id', this.props.company_id);
    if (this.state.statusFilter) params.set('status', this.state.statusFilter);

    fetch(`/api/ehc/service_orders?${params.toString()}`)
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load service orders');
        return res.json();
      })
      .then((data) => {
        const currentPage = data.pagination.page - 1;
        const totalCount = data.pagination.count;

        this.setState({
          statusCounts: data.status_counts || {},
          totalCount,
        });

        resolve({
          data: data.service_orders,
          page: currentPage,
          totalCount,
        });
      })
      .catch(reject);
  });

  refreshTable = () => {
    if (this.tableRef.current) this.tableRef.current.onQueryChange({ page: 0 });
  };

  handleStatusFilter = (status) => {
    this.setState({ statusFilter: status }, this.refreshTable);
  };

  handleSearchChange = (event) => {
    const value = event.target.value;
    this.setState({ searchInput: value });
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.setState({ searchQuery: value.trim() }, this.refreshTable);
    }, 350);
  };

  clearSearch = () => {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.setState({ searchInput: '', searchQuery: '' }, this.refreshTable);
  };

  renderStatusFilters = () => {
    const { statusCounts, statusFilter } = this.state;
    const total = Object.values(statusCounts).reduce((sum, count) => sum + count, 0);
    const filters = [
      { key: '', label: this.label('all_statuses', 'All'), count: total },
      { key: 'draft', label: this.label('status_draft', 'Draft'), count: statusCounts.draft || 0 },
      { key: 'in_progress', label: this.label('status_in_progress', 'In Progress'), count: statusCounts.in_progress || 0 },
      { key: 'completed', label: this.label('status_completed', 'Completed'), count: statusCounts.completed || 0 },
    ];

    return (
      <div className="ehc-service-orders-card__filters" role="group" aria-label={this.label('status', 'Status')}>
        {filters.map(({ key, label, count }) => (
          <button
            key={key || 'all'}
            type="button"
            className={`ehc-service-orders-card__filter-pill${statusFilter === key ? ' ehc-service-orders-card__filter-pill--active' : ''}`}
            onClick={() => this.handleStatusFilter(key)}
          >
            {label}
            <span className="ehc-service-orders-card__filter-count">{count}</span>
          </button>
        ))}
      </div>
    );
  };

  renderToolbar = () => (
    <div className="benefit-codes-card__header ehc-service-orders-card__header">
      {this.renderStatusFilters()}
      <div className="benefit-codes-card__actions">
        <div className="benefit-codes-card__search">
          <SearchIcon className="benefit-codes-card__search-icon" fontSize="small" />
          <input
            type="text"
            className="benefit-codes-card__search-input"
            placeholder={this.label('search_placeholder', 'Search service orders...')}
            value={this.state.searchInput}
            onChange={this.handleSearchChange}
          />
          {this.state.searchInput && (
            <button
              type="button"
              className="benefit-codes-card__search-clear"
              onClick={this.clearSearch}
              aria-label={this.label('clear_search', 'Clear search')}
            >
              <CloseIcon fontSize="small" />
            </button>
          )}
        </div>
        <button
          type="button"
          className="benefit-codes-card__btn benefit-codes-card__btn--icon"
          onClick={this.refreshTable}
          aria-label={this.label('refresh', 'Refresh Data')}
          title={this.label('refresh', 'Refresh Data')}
        >
          <RefreshIcon fontSize="small" />
        </button>
        {this.props.creatable && this.props.new_url && (
          <a href={this.props.new_url} className="benefit-codes-card__btn benefit-codes-card__btn--primary">
            <AddIcon fontSize="small" />
            <span>{this.label('new', 'New Service Order')}</span>
          </a>
        )}
      </div>
    </div>
  );

  render() {
    return (
      <MuiThemeProvider theme={this.getTheme()}>
        <div className="benefit-codes-card ehc-service-orders-card">
          {this.renderToolbar()}

          <div className="benefit-codes-card__table-wrap ehc-service-orders-card__table-wrap">
            <MaterialTable
              title=""
              columns={this.columns}
              tableRef={this.tableRef}
              data={this.fetchData}
              onRowClick={(_, rowData) => {
                if (rowData?.service_order_url) window.location.href = rowData.service_order_url;
              }}
              localization={{
                body: { emptyDataSourceMessage: this.label('empty', 'No service orders found') },
                pagination: { labelRowsSelect: this.label('rows', 'rows') },
              }}
              options={{
                pageSize: 20,
                pageSizeOptions: [20, 30, 50],
                exportButton: false,
                sorting: true,
                search: false,
                toolbar: false,
                grouping: false,
                emptyRowsWhenPaging: false,
                paginationType: 'normal',
                padding: 'default',
                tableLayout: 'auto',
                headerStyle: TABLE_HEADER_STYLE,
                rowStyle: { cursor: 'pointer', fontSize: '0.88rem' },
              }}
              style={{ boxShadow: 'none', background: 'transparent' }}
            />
          </div>
        </div>
      </MuiThemeProvider>
    );
  }
}
